import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LOCALE_NAMES, SUPPORTED_LOCALES } from '@zt/i18n';
import {
  Badge,
  Button,
  buttonStyles,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { Brand, ReadinessCheck, ReturnPolicy, Store, StoreDeleteImpact } from '../lib/types';
import { slugify } from './brands';
import { ReadinessList } from './payments';

const CHANNELS = ['HYBRID', 'B2C', 'B2B'] as const;
const CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'];
const COUNTRIES = ['TR', 'DE', 'NL', 'AT', 'BE', 'FR', 'GB', 'AZ', 'AE', 'SA', 'IQ', 'US'];
/** Pazarın vergi kipi (F7-13): ihracat istisnası yalnızca yurt dışı pazarda seçilebilir. */
const TAX_MODES = ['domestic', 'export_exempt', 'destination'] as const;
type TaxMode = (typeof TAX_MODES)[number];
const taxModesFor = (country: string) => TAX_MODES.filter((m) => country !== 'TR' || m !== 'export_exempt');
const statusTone: Record<string, Tone> = {
  draft: 'slate',
  live: 'moss',
  maintenance: 'amber',
  archived: 'neutral',
};

function useBrands() {
  return useQuery({ queryKey: ['brands'], queryFn: () => api.get<Brand[]>('/v1/admin/brands') });
}

function BrandChecklist({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const brands = useBrands();
  return (
    <div className="flex flex-wrap gap-2">
      {brands.data?.map((b) => {
        const on = value.includes(b.id);
        return (
          <label
            key={b.id}
            className={`flex cursor-pointer items-center gap-2 rounded-sm border px-3 py-2 text-sm ${on ? 'border-pine bg-pine-tint text-pine' : 'border-line-strong'}`}
          >
            <input
              type="checkbox"
              className="accent-[var(--zt-pine)]"
              checked={on}
              onChange={(e) =>
                onChange(e.target.checked ? [...value, b.id] : value.filter((x) => x !== b.id))
              }
            />
            {b.name}
          </label>
        );
      })}
    </div>
  );
}

function CreateStoreDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const t = useT();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);
  const [channelType, setChannel] = useState<(typeof CHANNELS)[number]>('HYBRID');
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const create = useApiMutation(
    () => api.post<Store>('/v1/admin/stores', { name, code, channelType, brandIds }),
    {
      invalidate: [['stores'], ['setup']],
      success: t('common.saved'),
      onSuccess: () => onOpenChange(false),
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      variant="sheet"
      title={t('admin.stores.createTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate(undefined)}>
            {t('common.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field label={t('admin.stores.name')} required error={fieldError(create.error, 'name')}>
          {(p) => (
            <Input
              {...p}
              value={name}
              placeholder="Zootopia Türkiye"
              onChange={(e) => {
                setName(e.target.value);
                if (!touched) setCode(slugify(e.target.value).slice(0, 40));
              }}
            />
          )}
        </Field>
        <Field
          label={t('admin.stores.code')}
          required
          error={
            fieldError(create.error, 'code') ??
            (create.error?.code === 'code_taken' ? create.error.title : undefined)
          }
        >
          {(p) => (
            <Input
              {...p}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setTouched(true);
              }}
            />
          )}
        </Field>
        <Field label={t('admin.stores.channel')} help={<HelpTip k="stores.channel_type" />}>
          {(p) => (
            <Select
              {...p}
              value={channelType}
              onChange={(e) => setChannel(e.target.value as (typeof CHANNELS)[number])}
            >
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.stores.channel${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('admin.stores.brands')}</p>
          <BrandChecklist value={brandIds} onChange={setBrandIds} />
        </div>
      </div>
    </Dialog>
  );
}

export function StoresPage() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('org.store.manage');
  const q = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  const [open, setOpen] = useState(false);
  return (
    <PageFrame
      helpKey="page.stores"
      actions={
        manage && (
          <div className="flex items-center gap-1">
            <Button variant="primary" onClick={() => setOpen(true)}>
              {t('admin.stores.add')}
            </Button>
            <HelpTip k="stores.create" />
          </div>
        )
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <EmptyState
          title={t('admin.stores.emptyTitle')}
          description={t('admin.stores.emptyLead')}
          action={
            manage && (
              <Button variant="primary" onClick={() => setOpen(true)}>
                {t('admin.stores.add')}
              </Button>
            )
          }
        />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {/* Arşivlenen mağazalar listenin sonunda. */}
          {[...q.data]
            .sort((a, b) => Number(a.status === 'archived') - Number(b.status === 'archived'))
            .map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <p className="font-medium">
                    {s.name} <span className="text-sm font-normal text-ink-3">({s.code})</span>
                  </p>
                  {s.domains.length ? (
                    <ul className="flex flex-wrap gap-1.5" aria-label={t('admin.stores.domains')}>
                      {s.domains.map((d) => (
                        <li
                          key={d.id}
                          className={`rounded-sm border px-2 py-0.5 text-sm break-all ${
                            d.isPrimary ? 'border-pine/40 bg-pine-tint text-pine' : 'border-line text-ink-2'
                          }`}
                        >
                          {d.hostname}
                          {d.isPrimary && <span className="sr-only"> ({t('admin.stores.primary')})</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-amber">{t('admin.stores.noDomains')}</p>
                  )}
                </div>
                <span className="text-sm text-ink-2">{t(`admin.stores.channel${s.channelType}`)}</span>
                <Badge tone={statusTone[s.status]}>{t(`admin.stores.status_${s.status}`)}</Badge>
                <Link to="/magazalar/$id" params={{ id: s.id }} className={buttonStyles({ size: 'sm' })}>
                  {t('admin.stores.open')}
                </Link>
              </li>
            ))}
        </ul>
      )}
      {open && <CreateStoreDialog open={open} onOpenChange={setOpen} />}
    </PageFrame>
  );
}

export function StoreDetailPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { id } = useParams({ strict: false }) as { id: string };
  const { can } = usePermissions();
  const manage = can('org.store.manage');
  const q = useQuery({ queryKey: ['stores', id], queryFn: () => api.get<Store>(`/v1/admin/stores/${id}`) });
  const inv = [['stores'], ['stores', id], ['setup']];

  const [basics, setBasics] = useState<{
    name: string;
    channelType: string;
    brandIds: string[];
  } | null>(null);
  const [host, setHost] = useState('');
  const [purpose, setPurpose] = useState('storefront');
  const [primary, setPrimary] = useState(true);
  const [market, setMarket] = useState({
    name: 'Türkiye',
    countryCode: 'TR',
    locale: 'tr',
    currency: 'TRY',
    taxMode: 'domestic' as TaxMode,
  });

  const saveBasics = useApiMutation(
    () => api.patch(`/v1/admin/stores/${id}`, basics, { ifMatch: q.data?.version }),
    {
      invalidate: inv,
      success: t('common.saved'),
      onSuccess: () => setBasics(null),
    },
  );
  const addDomain = useApiMutation(
    () => api.post(`/v1/admin/stores/${id}/domains`, { hostname: host, purpose, isPrimary: primary }),
    {
      invalidate: inv,
      success: t('common.saved'),
      onSuccess: () => setHost(''),
      silentFieldErrors: true,
    },
  );
  const removeDomain = useApiMutation(
    (domainId: string) => api.del(`/v1/admin/stores/${id}/domains/${domainId}`),
    { invalidate: inv },
  );
  const addMarket = useApiMutation(
    () => api.post(`/v1/admin/stores/${id}/markets`, { ...market, isDefault: !q.data?.markets.length }),
    {
      invalidate: inv,
      success: t('common.saved'),
    },
  );
  const updateMarket = useApiMutation(
    (m: { marketId: string; taxMode: TaxMode }) =>
      api.patch(`/v1/admin/stores/${id}/markets/${m.marketId}`, { taxMode: m.taxMode }),
    { invalidate: inv, success: t('admin.stores.taxModeSaved') },
  );
  const removeMarket = useApiMutation(
    (marketId: string) => api.del(`/v1/admin/stores/${id}/markets/${marketId}`),
    { invalidate: inv },
  );
  // Varsayılan pazar (F7-11): vitrin bu pazarın dili ve para birimiyle açılır.
  const defaultMarket = useApiMutation(
    (m: { marketId: string; version: number }) =>
      api.patch(`/v1/admin/stores/${id}`, { defaultMarketId: m.marketId }, { ifMatch: m.version }),
    { invalidate: inv, success: t('admin.stores.defaultMarketSaved') },
  );

  if (!q.data)
    return (
      <PageFrame helpKey="page.stores">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const s = q.data;
  const b = basics ?? {
    name: s.name,
    channelType: s.channelType,
    brandIds: s.brandIds,
  };

  return (
    <PageFrame
      helpKey="page.stores"
      title={s.name}
      actions={
        <Link to="/magazalar" className={buttonStyles({ variant: 'ghost' })}>
          {t('admin.stores.back')}
        </Link>
      }
    >
      <Section
        title={t('common.edit')}
        action={<Badge tone={statusTone[s.status]}>{t(`admin.stores.status_${s.status}`)}</Badge>}
      >
        <fieldset disabled={!manage} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t('admin.stores.name')} required>
              {(p) => (
                <Input {...p} value={b.name} onChange={(e) => setBasics({ ...b, name: e.target.value })} />
              )}
            </Field>
            <Field label={t('admin.stores.channel')} help={<HelpTip k="stores.channel_type" />}>
              {(p) => (
                <Select
                  {...p}
                  value={b.channelType}
                  onChange={(e) => setBasics({ ...b, channelType: e.target.value })}
                >
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {t(`admin.stores.channel${c}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('admin.stores.brands')}</p>
            <BrandChecklist value={b.brandIds} onChange={(v) => setBasics({ ...b, brandIds: v })} />
          </div>
          {basics && (
            <div className="flex justify-end gap-2">
              <Button onClick={() => setBasics(null)}>{t('common.cancel')}</Button>
              <Button
                variant="primary"
                loading={saveBasics.isPending}
                onClick={() => saveBasics.mutate(undefined)}
              >
                {t('common.save')}
              </Button>
            </div>
          )}
        </fieldset>
      </Section>

      <StoreStatusSection store={s} />

      <Section title={t('admin.stores.domains')} description={t('admin.stores.dnsNote')}>
        {s.domains.length === 0 ? (
          <p className="mb-4 text-ink-2">{t('admin.stores.noDomains')}</p>
        ) : (
          <ul className="mb-4 divide-y divide-line rounded-md border border-line bg-surface">
            {s.domains.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                <span className="flex-1 font-medium break-all">{d.hostname}</span>
                <span className="text-sm text-ink-2">{t(`admin.stores.purpose_${d.purpose}`)}</span>
                {d.isPrimary && <Badge tone="pine">{t('admin.stores.primary')}</Badge>}
                <span className="text-sm text-ink-3">
                  {t('admin.stores.tls')}:{' '}
                  {d.tlsExpiresAt ? formatDate(d.tlsExpiresAt, false) : t('common.unknown')}
                </span>
                {manage && (
                  <div className="flex items-center gap-1">
                    <ConfirmButton message={`${d.hostname}`} onConfirm={() => removeDomain.mutateAsync(d.id)}>
                      {t('admin.stores.remove')}
                    </ConfirmButton>
                    <HelpTip k="stores.remove_domain" />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {manage && (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_10rem_auto_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              addDomain.mutate(undefined);
            }}
          >
            <Field
              label={t('admin.stores.hostname')}
              error={
                fieldError(addDomain.error, 'hostname') ??
                (addDomain.error?.code === 'hostname_taken' ? addDomain.error.title : undefined)
              }
            >
              {(p) => (
                <Input
                  {...p}
                  value={host}
                  placeholder="zootopia.com.tr"
                  onChange={(e) => setHost(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('admin.stores.purpose')}>
              {(p) => (
                <Select {...p} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                  {['storefront', 'admin', 'api'].map((x) => (
                    <option key={x} value={x}>
                      {t(`admin.stores.purpose_${x}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <label className="flex h-10 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-[var(--zt-pine)]"
                checked={primary}
                onChange={(e) => setPrimary(e.target.checked)}
              />
              {t('admin.stores.primary')}
            </label>
            <div className="flex items-center gap-1">
              <Button type="submit" variant="primary" loading={addDomain.isPending} disabled={!host}>
                {t('admin.stores.addDomain')}
              </Button>
              <HelpTip k="stores.add_domain" />
            </div>
          </form>
        )}
      </Section>

      <Section title={t('admin.stores.markets')}>
        {s.markets.length === 0 ? (
          <p className="mb-4 text-ink-2">{t('admin.stores.noMarkets')}</p>
        ) : (
          <ul className="mb-4 divide-y divide-line rounded-md border border-line bg-surface">
            {s.markets.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                <span className="flex-1 font-medium">{m.name}</span>
                <dl className="flex gap-4 text-sm">
                  {(
                    [
                      [t('admin.stores.country'), m.countryCode],
                      [
                        t('admin.stores.language'),
                        LOCALE_NAMES[m.locale as keyof typeof LOCALE_NAMES] ?? m.locale,
                      ],
                      [t('admin.stores.currency'), m.currency],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="flex gap-1.5">
                      <dt className="text-ink-3">{label}</dt>
                      <dd className="tabular font-medium text-ink-2">{value}</dd>
                    </div>
                  ))}
                </dl>
                <span className="inline-flex items-center gap-1">
                  <Select
                    aria-label={t('admin.stores.taxModeOf', { name: m.name })}
                    className="w-auto"
                    value={m.taxMode}
                    disabled={!manage || updateMarket.isPending}
                    onChange={(e) =>
                      updateMarket.mutate({ marketId: m.id, taxMode: e.target.value as TaxMode })
                    }
                  >
                    {taxModesFor(m.countryCode).map((x) => (
                      <option key={x} value={x}>
                        {t(`admin.stores.taxModes.${x}`)}
                      </option>
                    ))}
                  </Select>
                  <HelpTip k="stores.market_tax_mode" />
                </span>
                {m.id === s.defaultMarketId && <Badge tone="pine">{t('admin.stores.default')}</Badge>}
                {manage && m.id !== s.defaultMarketId && (
                  <span className="inline-flex items-center gap-1">
                    <ConfirmButton
                      variant="secondary"
                      message={t('admin.stores.makeDefaultConfirm', { name: m.name, currency: m.currency })}
                      onConfirm={() => defaultMarket.mutateAsync({ marketId: m.id, version: s.version })}
                    >
                      {t('admin.stores.makeDefault')}
                    </ConfirmButton>
                    <HelpTip k="stores.default_market" />
                  </span>
                )}
                {manage && m.id !== s.defaultMarketId && (
                  <ConfirmButton message={m.name} onConfirm={() => removeMarket.mutateAsync(m.id)}>
                    {t('admin.stores.remove')}
                  </ConfirmButton>
                )}
              </li>
            ))}
          </ul>
        )}
        {manage && (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_6rem_8rem_7rem_11rem_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              addMarket.mutate(undefined);
            }}
          >
            <Field label={t('admin.stores.marketName')}>
              {(p) => (
                <Input
                  {...p}
                  value={market.name}
                  onChange={(e) => setMarket({ ...market, name: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('admin.stores.country')}>
              {(p) => (
                <Select
                  {...p}
                  value={market.countryCode}
                  onChange={(e) =>
                    setMarket({
                      ...market,
                      countryCode: e.target.value,
                      taxMode:
                        e.target.value === 'TR' && market.taxMode === 'export_exempt'
                          ? 'domestic'
                          : market.taxMode,
                    })
                  }
                >
                  {COUNTRIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.stores.language')}>
              {(p) => (
                <Select
                  {...p}
                  value={market.locale}
                  onChange={(e) => setMarket({ ...market, locale: e.target.value })}
                >
                  {SUPPORTED_LOCALES.map((l) => (
                    <option key={l} value={l}>
                      {LOCALE_NAMES[l]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.stores.currency')}>
              {(p) => (
                <Select
                  {...p}
                  value={market.currency}
                  onChange={(e) => setMarket({ ...market, currency: e.target.value })}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.stores.taxMode')} help={<HelpTip k="stores.market_tax_mode" />}>
              {(p) => (
                <Select
                  {...p}
                  value={market.taxMode}
                  onChange={(e) => setMarket({ ...market, taxMode: e.target.value as TaxMode })}
                >
                  {taxModesFor(market.countryCode).map((x) => (
                    <option key={x} value={x}>
                      {t(`admin.stores.taxModes.${x}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="flex items-center gap-1">
              <Button type="submit" variant="primary" loading={addMarket.isPending}>
                {t('admin.stores.addMarket')}
              </Button>
              <HelpTip k="stores.add_market" />
            </div>
          </form>
        )}
      </Section>
      <ReturnPolicySection storeId={id} canWrite={manage} />
      <CustomerAuthSection
        storeId={id}
        hosts={s.domains
          .filter((d) => d.purpose === 'storefront')
          .map((d) => d.hostname)
          .sort()
          .join(',')}
      />
      {can('org.store.go_live') && <DeleteStoreSection store={s} />}
    </PageFrame>
  );
}

/**
 * Boş mağazayı silme (F3B-14): yalnızca önizlemedeki/arşivdeki ve siparişi olmayan mağaza. Birlikte silinecek
 * kayıtlar listelenir; mağaza kodu yazılmadan silinmez (geri alınamaz, iki adımlı doğrulama ister).
 */
function DeleteStoreSection({ store: s }: { store: Store }) {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const impact = useQuery({
    queryKey: ['store-delete-impact', s.id],
    queryFn: () => api.get<StoreDeleteImpact>(`/v1/admin/stores/${s.id}/delete-impact`),
  });
  const del = useApiMutation(() => api.post(`/v1/admin/stores/${s.id}/delete`, { confirmCode: code }), {
    invalidate: [['stores'], ['setup']],
    success: t('admin.stores.deleted'),
    onSuccess: () => void navigate({ to: '/magazalar' }),
    silentFieldErrors: true,
  });
  const d = impact.data;
  if (!d) return null;
  const counts = Object.entries(d.counts).filter(([k, n]) => k !== 'orders' && n > 0);
  return (
    <Section title={t('admin.stores.deleteTitle')} description={t('admin.stores.deleteLead')}>
      {d.deletable ? (
        <span className="flex items-center gap-1">
          <Button variant="danger" onClick={() => setOpen(true)}>
            {t('admin.stores.delete')}
          </Button>
          <HelpTip k="stores.delete" />
        </span>
      ) : (
        <ul className="list-disc space-y-1 ps-5 text-sm text-ink-2">
          {d.blockers.map((b) => (
            <li key={b}>{t(`admin.stores.deleteBlocked_${b}`)}</li>
          ))}
        </ul>
      )}
      {open && (
        <Dialog
          open
          onOpenChange={(v) => !v && setOpen(false)}
          title={t('admin.stores.deleteDialogTitle', { name: d.name })}
          description={t('admin.stores.deleteDialogLead')}
          closeLabel={t('common.close')}
          footer={
            <>
              <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
              <Button
                variant="danger"
                loading={del.isPending}
                disabled={code.trim() !== d.code}
                onClick={() => del.mutate(undefined)}
              >
                {t('admin.stores.delete')}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {counts.length > 0 && (
              <ul className="list-disc space-y-1 ps-5 text-sm text-ink-2">
                {counts.map(([k, n]) => (
                  <li key={k}>{t(`admin.stores.deleteCount_${k}`, { count: n })}</li>
                ))}
              </ul>
            )}
            <Field
              label={t('admin.stores.deleteCodeLabel', { code: d.code })}
              help={<HelpTip k="stores.delete_code" />}
              error={fieldError(del.error, 'confirmCode')}
            >
              {(p) => (
                <Input {...p} value={code} autoComplete="off" onChange={(e) => setCode(e.target.value)} />
              )}
            </Field>
            {del.error && !del.error.errors.length && (
              <p
                role="alert"
                className="rounded-sm border border-rose/30 bg-rose-tint px-3 py-2 text-sm text-rose"
              >
                {del.error.title}
              </p>
            )}
          </div>
        </Dialog>
      )}
    </Section>
  );
}

type StoreStatus = 'draft' | 'live' | 'maintenance' | 'archived';

/**
 * Yayın durumu: önizleme → yayında (hazırlık listesi + açık onay + iki adımlı doğrulama), bakım, önizlemeye dönüş.
 * Her değişiklik denetim kaydına yazılır.
 */
function StoreStatusSection({ store: s }: { store: Store }) {
  const t = useT();
  const { can } = usePermissions();
  const allowed = can('org.store.go_live');
  const [target, setTarget] = useState<StoreStatus | null>(null);
  const readiness = useQuery({
    queryKey: ['readiness', s.id],
    queryFn: () => api.get<ReadinessCheck[]>(`/v1/admin/stores/${s.id}/readiness`),
    enabled: s.status !== 'live',
  });
  const blocking = (readiness.data ?? []).filter((c) => c.required && !c.ok);
  return (
    <Section
      title={t('admin.launch.title')}
      description={t(`admin.launch.lead_${s.status}`)}
      action={
        <div className="flex items-center gap-1">
          <Badge tone={statusTone[s.status]}>{t(`admin.stores.status_${s.status}`)}</Badge>
          <HelpTip k="stores.status" />
        </div>
      }
    >
      {s.status !== 'live' && s.status !== 'archived' && <ReadinessList storeId={s.id} />}
      {allowed ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {s.status !== 'live' && s.status !== 'archived' && (
            <>
              <Button
                variant="primary"
                disabled={!readiness.data || blocking.length > 0}
                onClick={() => setTarget('live')}
              >
                {t('admin.launch.goLive')}
              </Button>
              <HelpTip k="stores.go_live" />
            </>
          )}
          {s.status === 'live' && (
            <>
              <Button onClick={() => setTarget('maintenance')}>{t('admin.launch.toMaintenance')}</Button>
              <HelpTip k="stores.maintenance" />
            </>
          )}
          {s.status !== 'draft' && (
            <>
              <Button variant="ghost" onClick={() => setTarget('draft')}>
                {s.status === 'archived' ? t('admin.launch.unarchive') : t('admin.launch.toDraft')}
              </Button>
              <HelpTip k="stores.to_draft" />
            </>
          )}
          {(s.status === 'draft' || s.status === 'maintenance') && (
            <>
              <Button variant="ghost" onClick={() => setTarget('archived')}>
                {t('admin.launch.toArchive')}
              </Button>
              <HelpTip k="stores.archive" />
            </>
          )}
          {s.status !== 'live' && readiness.data && blocking.length > 0 && (
            <p className="w-full text-sm text-ink-2">
              {t('admin.launch.blocked', { count: blocking.length })}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-4 text-sm text-ink-3">{t('admin.launch.noPermission')}</p>
      )}
      {target && <StoreStatusDialog store={s} target={target} onClose={() => setTarget(null)} />}
    </Section>
  );
}

function StoreStatusDialog({
  store: s,
  target,
  onClose,
}: {
  store: Store;
  target: StoreStatus;
  onClose: () => void;
}) {
  const t = useT();
  const [confirm, setConfirm] = useState(false);
  const [note, setNote] = useState('');
  const m = useApiMutation(
    () =>
      api.post(
        `/v1/admin/stores/${s.id}/status`,
        { status: target, confirm: target === 'live' ? confirm : undefined, note: note || null },
        { ifMatch: s.version },
      ),
    {
      invalidate: [['stores'], ['stores', s.id], ['setup'], ['readiness', s.id]],
      success: t(`admin.launch.done_${target}`),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const err = m.error && !m.error.errors.length ? m.error.title : fieldError(m.error, 'confirm');
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`admin.launch.dialogTitle_${target}`)}
      description={t(`admin.launch.dialogLead_${target}`)}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={target === 'live' ? 'primary' : 'danger'}
            loading={m.isPending}
            disabled={target === 'live' && !confirm}
            onClick={() => m.mutate(undefined)}
          >
            {t(`admin.launch.confirm_${target}`)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {target === 'live' && (
          <>
            <ul className="list-disc space-y-1 ps-5 text-sm text-ink-2">
              <li>{t('admin.launch.liveEffect1')}</li>
              <li>{t('admin.launch.liveEffect2')}</li>
              <li>{t('admin.launch.liveEffect3')}</li>
            </ul>
            <div className="flex items-start gap-1">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 accent-[var(--zt-pine)]"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                />
                <span>{t('admin.launch.confirmText')}</span>
              </label>
              <HelpTip k="stores.go_live_confirm" />
            </div>
          </>
        )}
        <Field
          label={t('admin.launch.note')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="stores.status_note" />}
        >
          {(p) => <Input {...p} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        {err && (
          <p
            role="alert"
            className="rounded-sm border border-rose/30 bg-rose-tint px-3 py-2 text-sm text-rose"
          >
            {err}
          </p>
        )}
      </div>
    </Dialog>
  );
}

type ReturnPolicyForm = {
  enabled: boolean;
  days: string;
  method: 'by_mail' | 'in_store' | 'by_mail_or_in_store';
  fees: 'free' | 'customer_pays';
};

/**
 * İade politikası özeti (F5-03): arama motorlarına ve Merchant Center'a bildirilir. Yasal metinlerdeki
 * cayma/iade koşullarıyla aynı olmalıdır; açılmadıkça yapılandırılmış veride iade politikası yazılmaz.
 */
interface CustomerAuthView {
  settings: { googleSignIn: boolean };
  configured: boolean;
  redirectUris: string[];
}

/**
 * Müşteri girişi (F7-29): "Google ile giriş" mağaza başına açılır (varsayılan kapalı). Açmak yalnızca işletme
 * sahibi (iki adımlı doğrulamayla) ve Entegrasyonlar'da "Google ile giriş" istemcisi kuruluyken; Google Cloud'daki
 * istemciye girilecek dönüş adresleri listelenir.
 */
function CustomerAuthSection({ storeId, hosts }: { storeId: string; hosts: string }) {
  const t = useT();
  const { can, roleKey } = usePermissions();
  // Dönüş adresleri vitrin alan adlarından gelir: alan adı eklenince/kaldırılınca yeniden okunur.
  const q = useQuery({
    queryKey: ['customer-auth', storeId, hosts],
    queryFn: () => api.get<CustomerAuthView>(`/v1/admin/stores/${storeId}/customer-auth`),
  });
  const save = useApiMutation(
    (googleSignIn: boolean) => api.put(`/v1/admin/stores/${storeId}/customer-auth`, { googleSignIn }),
    { invalidate: [['customer-auth', storeId]], success: t('common.saved') },
  );
  if (!q.data) return null;
  const on = q.data.settings.googleSignIn;
  const canEnable = roleKey === 'owner' && q.data.configured;
  return (
    <Section
      title={t('admin.stores.customerAuth.title')}
      description={t('admin.stores.customerAuth.lead')}
      action={<HelpTip k="stores.customer_auth" />}
    >
      <div className="space-y-4 text-sm">
        <p className="flex flex-wrap items-center gap-2">
          <Badge tone={q.data.configured ? 'moss' : 'amber'}>
            {q.data.configured
              ? t('admin.stores.customerAuth.clientReady')
              : t('admin.stores.customerAuth.clientMissing')}
          </Badge>
          {!q.data.configured && (
            <Link to="/entegrasyonlar" className="font-medium text-pine underline underline-offset-2">
              {t('admin.stores.customerAuth.configure')}
            </Link>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-medium">{t('admin.stores.customerAuth.google')}</span>
          <Badge tone={on ? 'moss' : 'neutral'}>
            {on ? t('admin.stores.customerAuth.on') : t('admin.stores.customerAuth.off')}
          </Badge>
          {can('org.settings.update') &&
            (on ? (
              <span className="flex items-center gap-1">
                <ConfirmButton
                  variant="secondary"
                  message={t('admin.stores.customerAuth.disableConfirm')}
                  onConfirm={() => save.mutateAsync(false)}
                >
                  {t('admin.stores.customerAuth.disable')}
                </ConfirmButton>
                <HelpTip k="stores.customer_auth_toggle" />
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <ConfirmButton
                  variant="primary"
                  disabled={!canEnable}
                  message={t('admin.stores.customerAuth.enableConfirm')}
                  onConfirm={() => save.mutateAsync(true)}
                >
                  {t('admin.stores.customerAuth.enable')}
                </ConfirmButton>
                <HelpTip k="stores.customer_auth_toggle" />
              </span>
            ))}
        </div>
        {!on && roleKey !== 'owner' && (
          <p className="text-ink-3">{t('admin.stores.customerAuth.ownerOnly')}</p>
        )}
        <div>
          <p className="mb-1 flex items-center gap-1 font-medium">
            {t('admin.stores.customerAuth.redirects')} <HelpTip k="stores.customer_auth_redirects" />
          </p>
          {q.data.redirectUris.length ? (
            <ul className="space-y-1">
              {q.data.redirectUris.map((u) => (
                <li key={u}>
                  <code className="break-all rounded-sm bg-surface-2 px-1.5 py-0.5 text-[0.8125rem]">
                    {u}
                  </code>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-3">{t('admin.stores.customerAuth.noDomains')}</p>
          )}
        </div>
      </div>
    </Section>
  );
}

function ReturnPolicySection({ storeId, canWrite }: { storeId: string; canWrite: boolean }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['return-policy', storeId],
    queryFn: () => api.get<{ policy: ReturnPolicy | null }>(`/v1/admin/stores/${storeId}/return-policy`),
  });
  const [draft, setDraft] = useState<ReturnPolicyForm | null>(null);
  const saved = q.data?.policy;
  const f: ReturnPolicyForm = draft ?? {
    enabled: saved?.enabled ?? false,
    days: String(saved?.days ?? 14),
    method: saved?.method ?? 'by_mail',
    fees: saved?.fees ?? 'customer_pays',
  };
  const set = (patch: Partial<ReturnPolicyForm>) => setDraft({ ...f, ...patch });
  const save = useApiMutation(
    () =>
      api.put(`/v1/admin/stores/${storeId}/return-policy`, {
        enabled: f.enabled,
        days: Number(f.days),
        method: f.method,
        fees: f.fees,
      }),
    {
      invalidate: [['return-policy', storeId]],
      success: t('admin.stores.returnPolicy.saved'),
      onSuccess: () => setDraft(null),
      silentFieldErrors: true,
    },
  );
  if (!q.data) return null;
  return (
    <Section
      title={t('admin.stores.returnPolicy.title')}
      description={t('admin.stores.returnPolicy.lead')}
      action={<HelpTip k="stores.return_policy" />}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined);
        }}
      >
        <fieldset disabled={!canWrite} className="space-y-4">
          <span className="flex items-center gap-2">
            <Switch
              checked={f.enabled}
              onCheckedChange={(v) => set({ enabled: v })}
              label={t('admin.stores.returnPolicy.enabled')}
            />
            <span className="text-sm">{t('admin.stores.returnPolicy.enabled')}</span>
          </span>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label={t('admin.stores.returnPolicy.days')}
              error={fieldError(save.error, 'days')}
              help={<HelpTip k="stores.return_days" />}
            >
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={14}
                  max={365}
                  inputMode="numeric"
                  value={f.days}
                  onChange={(e) => set({ days: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('admin.stores.returnPolicy.method')} help={<HelpTip k="stores.return_method" />}>
              {(p) => (
                <Select
                  {...p}
                  value={f.method}
                  onChange={(e) => set({ method: e.target.value as ReturnPolicyForm['method'] })}
                >
                  {(['by_mail', 'in_store', 'by_mail_or_in_store'] as const).map((m) => (
                    <option key={m} value={m}>
                      {t(`admin.stores.returnPolicy.methods.${m}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.stores.returnPolicy.fees')} help={<HelpTip k="stores.return_fees" />}>
              {(p) => (
                <Select
                  {...p}
                  value={f.fees}
                  onChange={(e) => set({ fees: e.target.value as ReturnPolicyForm['fees'] })}
                >
                  <option value="customer_pays">
                    {t('admin.stores.returnPolicy.feeOptions.customer_pays')}
                  </option>
                  <option value="free">{t('admin.stores.returnPolicy.feeOptions.free')}</option>
                </Select>
              )}
            </Field>
          </div>
        </fieldset>
        {canWrite && (
          <Button type="submit" loading={save.isPending} disabled={draft === null}>
            {t('admin.stores.returnPolicy.save')}
          </Button>
        )}
      </form>
    </Section>
  );
}
